import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  getPublicChecklistShareInfo,
  verifyPublicChecklistShare,
  downloadPublicChecklistPdf,
  type PublicChecklistView,
} from '../../api/checklistsApi';
import { formatPhoneBr } from '../../utils/phoneMask';
import logoBackground from '../auth/pages/css/img/Atual Clube - Marca_Laranja 1.svg';
import logo from '../auth/pages/css/img/logo.svg';
import './ChecklistPublicoPage.css';

const STATUS_LABELS: Record<string, string> = {
  OK: 'OK',
  DAMAGED: 'Avariado',
  MISSING: 'Ausente',
  NA: 'N/A',
};

type MediaGroup = { title: string; items: any[] };

/** Agrupa as mídias de uma sessão por seção (ângulo, seção personalizada ou imagens de saída). */
const groupSessionMedia = (session: any): MediaGroup[] => {
  if (!session?.media?.length) return [];

  const slotsById = new Map<string, any>((session.slots || []).map((s: any) => [s.id, s]));
  const groups = new Map<string, MediaGroup>();

  for (const media of session.media) {
    const slot = media.slotId ? slotsById.get(media.slotId) : null;
    const title = slot
      ? (slot.label || slot.key)
      : media.folder === 'SERVICO'
        ? 'Imagens de saída'
        : 'Outras mídias';

    if (!groups.has(title)) groups.set(title, { title, items: [] });
    groups.get(title)!.items.push(media);
  }

  return Array.from(groups.values());
};

const MediaGroupView: React.FC<{ group: MediaGroup }> = ({ group }) => (
  <div className="cp-media-group">
    <div className="cp-media-group-title">{group.title}</div>
    <div className="cp-media-grid">
      {group.items.map((media: any) => (
        <div key={media.id} className="cp-media-item">
          {media.type === 'VIDEO' ? (
            <video src={media.url} controls preload="metadata" />
          ) : (
            <a href={media.url} target="_blank" rel="noopener noreferrer">
              <img src={media.url} alt={group.title} loading="lazy" />
            </a>
          )}
        </div>
      ))}
    </div>
  </div>
);

/** Observações gerais da sessão (at-50) — rótulo conforme entrada/saída. */
const SessionObservationView: React.FC<{ label: string; observation?: string | null }> = ({ label, observation }) => {
  if (!observation || !observation.trim()) return null;
  return (
    <div className="cp-observation">
      <div className="cp-observation-title">{label}</div>
      <div className="cp-observation-text">{observation}</div>
    </div>
  );
};

const SessionItemsView: React.FC<{ items: { templates: any[]; answers: any[] } }> = ({ items }) => {
  const answersByKey = useMemo(
    () => new Map((items?.answers || []).map((a: any) => [a.templateKey, a])),
    [items]
  );

  const byCategory = useMemo(() => {
    const out = new Map<string, any[]>();
    for (const template of items?.templates || []) {
      if (!answersByKey.has(template.key)) continue;
      const label = template.categoryLabel || 'Itens';
      if (!out.has(label)) out.set(label, []);
      out.get(label)!.push(template);
    }
    return out;
  }, [items, answersByKey]);

  if (!items?.answers?.length) return null;

  return (
    <div className="cp-items">
      {Array.from(byCategory.entries()).map(([categoryLabel, templates]) => (
        <div key={categoryLabel} className="cp-items-category">
          <div className="cp-items-category-title">{categoryLabel}</div>
          {templates.map((template: any) => {
            const answer: any = answersByKey.get(template.key);
            return (
              <div key={template.key} className="cp-item-row-wrapper">
                <div className="cp-item-row">
                  <span className="cp-item-label">{template.label}</span>
                  <span className={`cp-item-status cp-item-status--${String(answer.status).toLowerCase()}`}>
                    {STATUS_LABELS[answer.status] || answer.status}
                  </span>
                </div>
                {answer.note && (
                  <div className="cp-item-note">
                    <strong>Obs:</strong> {answer.note}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

/**
 * Página pública do checklist compartilhado via WhatsApp: valida o token do
 * link e pede o celular do proprietário antes de exibir o conteúdo.
 */
const ChecklistPublicoPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();

  const [linkStatus, setLinkStatus] = useState<'checking' | 'valid' | 'invalid'>('checking');
  const [linkError, setLinkError] = useState('');
  const [phone, setPhone] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState('');
  const [view, setView] = useState<PublicChecklistView | null>(null);
  const [verifiedPhone, setVerifiedPhone] = useState('');
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadError, setDownloadError] = useState('');
  const [linkExpiresAt, setLinkExpiresAt] = useState('');

  useEffect(() => {
    if (!token) {
      setLinkStatus('invalid');
      setLinkError('Link de checklist inválido.');
      return;
    }

    getPublicChecklistShareInfo(token)
      .then((info) => {
        setLinkStatus('valid');
        // at-72: a rota já devolvia `expiresAt` e o payload era descartado. A data vem
        // gravada no token, então link antigo mostra a validade com que nasceu — nunca
        // mente, mesmo se a configuração de prazo mudar depois.
        setLinkExpiresAt(info?.expiresAt || '');
      })
      .catch((e: any) => {
        setLinkStatus('invalid');
        setLinkError(e?.message || 'Link de checklist inválido ou expirado.');
      });
  }, [token]);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    const digits = phone.replace(/\D/g, '');
    if (digits.length < 10) {
      setVerifyError('Informe o celular completo, com DDD.');
      return;
    }

    setVerifying(true);
    setVerifyError('');
    try {
      const data = await verifyPublicChecklistShare(token, digits);
      setView(data);
      setVerifiedPhone(digits);
    } catch (err: any) {
      setVerifyError(err?.message || 'Não foi possível verificar o número informado.');
    } finally {
      setVerifying(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!token) return;

    setDownloadingPdf(true);
    setDownloadError('');
    try {
      const blob = await downloadPublicChecklistPdf(token, verifiedPhone);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `checklist-${view?.car.plate || 'veiculo'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setDownloadError(err?.message || 'Erro ao gerar o PDF do checklist.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const vehicleName = useMemo(() => {
    if (!view?.car) return '';
    const brand = view.car.marca || '';
    const model = view.car.model || '';
    if (brand && model.toLowerCase().startsWith(brand.toLowerCase())) {
      return model;
    }
    return [brand, model].filter(Boolean).join(' ');
  }, [view]);

  return (
    <div className="cp-page">
      <div className="logo-background">
        <img className="logo-background-atual" src={logoBackground} alt="logo de fundo" />
      </div>

      <div className="cp-container-wrapper">
        <img className="cp-logo" src={logo} alt="Logo Atual" />

        <div className={`cp-container ${!view ? 'cp-container--auth' : 'cp-container--full'}`}>
          <div className="cp-header">
            <h1 className="cp-title">Checklist do Veículo</h1>
            {linkStatus === 'valid' && linkExpiresAt && (
              <span className="cp-link-expiry">
                Link válido até {new Date(linkExpiresAt).toLocaleDateString('pt-BR')}
              </span>
            )}
            {view && (
              <div className="cp-vehicle-card">
                <div className="cp-vehicle-details">
                  <span className="cp-vehicle-label">Veículo</span>
                  <span className="cp-vehicle-name">{vehicleName}</span>
                  {view.car.ownerName && (
                    <span className="cp-vehicle-owner">Proprietário: {view.car.ownerName}</span>
                  )}
                  {view.car.color && (
                    <span className="cp-vehicle-color">Cor: {view.car.color}</span>
                  )}
                </div>
                <div className="cp-plate-badge">
                  <div className="cp-plate-header">
                    <span>BRASIL</span>
                  </div>
                  <div className="cp-plate-number">{view.car.plate}</div>
                </div>
              </div>
            )}

            {view && (
              <p className="cp-hint">
                As informações desta página são só do seu atendimento. Evite encaminhar este
                link — quem abrir vai precisar confirmar o mesmo número de celular cadastrado
                na oficina para conseguir ver o conteúdo.
              </p>
            )}

            {view && (
              <div className="cp-actions">
                <button
                  type="button"
                  className="cp-btn-download"
                  onClick={handleDownloadPdf}
                  disabled={downloadingPdf}
                >
                  {downloadingPdf ? 'Gerando PDF...' : 'Baixar PDF'}
                </button>
                <p className="cp-hint">O PDF reúne tudo em um único documento, para guardar ou encaminhar.</p>
                {downloadError && <div className="cp-error-box">{downloadError}</div>}
              </div>
            )}
          </div>

          {linkStatus === 'checking' && (
            <div className="cp-info cp-info--loading">
              <div className="cp-spinner"></div>
              <span>Validando link...</span>
            </div>
          )}

          {linkStatus === 'invalid' && (
            <div className="cp-error-box">{linkError}</div>
          )}

          {linkStatus === 'valid' && !view && (
            <form className="cp-form-auth" onSubmit={handleVerify}>
              <div className="cp-auth-div">
                <div className="cp-auth-title">
                  <h1 className="cp-h1-auth">Acessar Checklist</h1>
                  <p className="cp-p-auth">
                    Confirme o número do celular cadastrado na oficina para visualizar o checklist.
                  </p>
                  <p className="cp-hint">
                    Esta verificação protege as informações do seu veículo: só quem tem o celular cadastrado na
                    oficina consegue abrir. Se o número não for aceito, entre em contato com a oficina para
                    atualizar o cadastro.
                  </p>
                </div>

                <div className="cp-auth-fields">
                  <p className="cp-field-label">Celular</p>

                  <div className="nice-form-group auth">
                    <input
                      type="tel"
                      placeholder="(21) 99999-8888"
                      value={phone}
                      onChange={(e) => setPhone(formatPhoneBr(e.target.value))}
                      autoFocus
                      required
                      className="nice-input"
                    />
                  </div>
                </div>

                {verifyError && <div className="cp-error-box">{verifyError}</div>}

                <div className="cp-button-div auth">
                  <button type="submit" disabled={verifying} className="cp-btn-primary">
                    {verifying ? 'Verificando...' : 'Ver Checklist'}
                  </button>
                </div>
              </div>
            </form>
          )}

          {view && (
            <div className="cp-content">
              <p className="cp-hint">
                Este é o registro de vistoria do seu veículo feito pela oficina. O{' '}
                <strong>Checklist de entrada</strong> mostra como o veículo chegou — cada item vistoriado aparece
                como <strong>OK</strong>, <strong>Avariado</strong>, <strong>Ausente</strong> ou{' '}
                <strong>N/A</strong> (não se aplica) — e <strong>Saída do veículo</strong> mostra as fotos de como
                ele foi devolvido. Toque nas fotos para ampliá-las.
              </p>
              {view.entrada && (
                <section className="cp-session">
                  <h2 className="cp-session-title">Checklist de entrada</h2>
                  {view.entrada.completedAt && (
                    <div className="cp-session-date">
                      Concluído em {new Date(view.entrada.completedAt).toLocaleString('pt-BR')}
                    </div>
                  )}
                  <SessionItemsView items={view.entrada.items} />
                  {groupSessionMedia(view.entrada).map((group) => (
                    <MediaGroupView key={group.title} group={group} />
                  ))}
                  <SessionObservationView label="Observações de Entrada" observation={view.entrada.observation} />
                </section>
              )}

              {view.saida && (
                <section className="cp-session">
                  <h2 className="cp-session-title">Saída do veículo</h2>
                  {view.saida.completedAt && (
                    <div className="cp-session-date">
                      Concluído em {new Date(view.saida.completedAt).toLocaleString('pt-BR')}
                    </div>
                  )}
                  {groupSessionMedia(view.saida).map((group) => (
                    <MediaGroupView key={group.title} group={group} />
                  ))}
                  <SessionObservationView label="Observações de Saída" observation={view.saida.observation} />
                </section>
              )}

              {!view.entrada && !view.saida && (
                <div className="cp-info">Nenhum checklist disponível para este veículo.</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChecklistPublicoPage;
